import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { apiClient, type PublicConfig } from '@/lib/api-client';

interface FeatureFlagsContextValue {
  publicConfig: PublicConfig | null;
  ready: boolean;
  isEnabled: (flag: string) => boolean;
}

// Fail-closed: until the initial fetch resolves, every flag reads as
// disabled so gated UI never flashes on before hiding.
const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  publicConfig: null,
  ready: false,
  isEnabled: () => false,
});

function evaluateFlag(flags: Record<string, boolean>, flag: string): boolean {
  const negate = flag.startsWith('!');
  const flagName = negate ? flag.slice(1) : flag;
  const value = !!flags[flagName];
  return negate ? !value : value;
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getPublicConfig()
      .then((cfg) => {
        if (!cancelled) setPublicConfig(cfg);
      })
      .catch(() => {
        if (!cancelled) setPublicConfig(null);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isEnabled = useCallback(
    (flag: string) => evaluateFlag(publicConfig?.feature_flags || {}, flag),
    [publicConfig]
  );

  const value = useMemo(
    () => ({ publicConfig, ready, isEnabled }),
    [publicConfig, ready, isEnabled]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  return useContext(FeatureFlagsContext);
}

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

export function FeatureFlagRoute({
  flag,
  fallback = '/dashboard/agents',
  children,
}: {
  flag: string;
  fallback?: string;
  children: ReactNode;
}) {
  const { isEnabled, ready } = useFeatureFlags();
  if (!ready) return <PageFallback />;
  if (!isEnabled(flag)) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}
